<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { debounce } from 'lodash'
import { bbox, simplify } from '@turf/turf'

import { MAPBOX_ACCESS_TOKEN, MAPBOX_API } from '@/config/mapbox'
import { searchPlaces } from '@/utils/nominatim'
import { API, HOST } from '@/utils/http'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { toast } from '@/components/ui/toast'

import { Search as SearchIcon, MapPin, X, Loader2, FolderDot, Eye, ArrowLeft, Plus, Square, ExternalLink } from 'lucide-vue-next'

const PREVIEW_SOURCE = 'mapsearch-preview'
const SIMPLIFY_TOLERANCE = 0.001

type GeomType = 'Point' | 'Polygon' | 'MultiPolygon'

interface UnifiedResult {
  id: string
  name: string
  geometryType: GeomType
  geometry: any
  flyBbox?: [number, number, number, number]
  flyCenter?: [number, number]
  // set only for backend features
  projectId?: number
  orgId?: number
}

interface Project {
  project_id: number
  project_name: string
  organization_id: number
}

const props = defineProps<{ fullWidth?: boolean }>()

const store = useStore()
const router = useRouter()
const map = computed(() => store.getters['geo/getMap'])
const member = computed(() => store.getters['user/getMember'])
const projects = computed<Project[]>(() => store.getters['project/getProjects'] || [])
const currentOrgId = computed(() => store.getters['org/getSelected'])

const query = ref('')
const open = ref(false)
const loading = ref(false)

const rawFeatures = ref<any[]>([])
const rawMapbox = ref<any[]>([])
const rawNominatim = ref<any[]>([])

const mode = ref<'search' | 'pick'>('search')
const pickingFor = ref<UnifiedResult | null>(null)
const previewingId = ref<string | null>(null)
const saving = ref(false)

const focused = ref(false)
const isExpanded = computed(() => focused.value || open.value)

const activeIndex = ref(-1)
const rowRefs = ref<HTMLElement[]>([])

let currentAbort: AbortController | null = null

const SUPPORTED: GeomType[] = ['Point', 'Polygon', 'MultiPolygon']
const GEOM_ORDER: Record<GeomType, number> = { Point: 0, Polygon: 1, MultiPolygon: 2 }
const RESULT_GROUPS: { label: string; types: GeomType[]; icon: string }[] = [
  { label: 'Points', types: ['Point'], icon: 'pin' },
  { label: 'Polygons', types: ['Polygon', 'MultiPolygon'], icon: 'square' },
]

const results = computed<UnifiedResult[]>(() => {
  const out: UnifiedResult[] = []

  for (const f of rawFeatures.value) {
    const geom = f.geometry
    if (!geom || !SUPPORTED.includes(geom.type)) continue
    const geomType: GeomType = geom.type
    let flyBbox: [number, number, number, number] | undefined
    let flyCenter: [number, number] | undefined
    if (geomType === 'Point') {
      flyCenter = geom.coordinates
    } else {
      const bounds = bbox({ type: 'Feature', properties: {}, geometry: geom })
      flyBbox = bounds as [number, number, number, number]
    }
    out.push({ id: f.id, name: f.name, geometryType: geomType, geometry: geom, flyBbox, flyCenter, projectId: f.project_id, orgId: f.org_id })
  }

  for (const f of rawMapbox.value) {
    out.push({
      id: `mb-${f.id}`,
      name: f.place_name,
      geometryType: 'Point',
      geometry: { type: 'Point', coordinates: f.center },
      flyBbox: f.bbox?.length === 4 ? f.bbox : undefined,
      flyCenter: f.center
    })
  }

  for (const f of rawNominatim.value) {
    const geomType: string = f.geojson?.type ?? 'Point'
    if (!SUPPORTED.includes(geomType as GeomType)) continue
    const geometry = f.geojson ?? { type: 'Point', coordinates: [parseFloat(f.lon), parseFloat(f.lat)] }
    let flyBbox: [number, number, number, number] | undefined
    if (geomType !== 'Point' && f.geojson) {
      const bounds = bbox({ type: 'Feature', properties: {}, geometry: f.geojson })
      flyBbox = bounds as [number, number, number, number]
    } else if (f.boundingbox) {
      const [s, n, w, e] = f.boundingbox.map(parseFloat)
      flyBbox = [w, s, e, n]
    }
    out.push({
      id: `nm-${f.place_id}`,
      name: f.display_name,
      geometryType: geomType as GeomType,
      geometry,
      flyBbox,
      flyCenter: [parseFloat(f.lon), parseFloat(f.lat)]
    })
  }

  return out.sort((a, b) => GEOM_ORDER[a.geometryType] - GEOM_ORDER[b.geometryType])
})

watch(results, () => { activeIndex.value = -1 })

const onKeydown = (e: KeyboardEvent) => {
  if (mode.value !== 'search' || !open.value) return
  const len = results.value.length
  if (!len) return
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = Math.min(activeIndex.value + 1, len - 1)
    nextTick(() => rowRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
    nextTick(() => rowRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' }))
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    selectResult(results.value[activeIndex.value])
  }
}

// ─── Search ────────────────────────────────────────────────────────────────

const runSearch = async (value: string) => {
  if (currentAbort) currentAbort.abort()
  const ctrl = new AbortController()
  currentAbort = ctrl

  loading.value = true
  try {
    const [featuresRes, mapboxRes, nominatimRes] = await Promise.allSettled([
      currentOrgId.value
        ? API({ url: `${HOST}/org/${currentOrgId.value}/search`, method: 'GET', params: { q: value }, signal: ctrl.signal })
        : Promise.resolve(null),
      axios.get(`${MAPBOX_API.GEOCODING}/${encodeURIComponent(value)}.json`, {
        params: { access_token: MAPBOX_ACCESS_TOKEN, autocomplete: true, limit: 5 },
        signal: ctrl.signal
      }),
      searchPlaces(value, { limit: 5, signal: ctrl.signal })
    ])

    if (ctrl.signal.aborted) return

    rawFeatures.value = featuresRes.status === 'fulfilled' && featuresRes.value
      ? (featuresRes.value.data?.suggestions ?? []).filter((s: any) => s.type === 'feature')
      : []
    rawMapbox.value = mapboxRes.status === 'fulfilled' ? (mapboxRes.value.data?.features ?? []) : []
    rawNominatim.value = nominatimRes.status === 'fulfilled' ? (nominatimRes.value ?? []) : []
  } finally {
    if (!ctrl.signal.aborted) loading.value = false
  }
}

const debouncedSearch = debounce((value: string) => runSearch(value), 300)

const onInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value
  if (!value.trim()) {
    debouncedSearch.cancel()
    if (currentAbort) currentAbort.abort()
    rawFeatures.value = []
    rawMapbox.value = []
    rawNominatim.value = []
    loading.value = false
    open.value = false
    return
  }
  open.value = true
  debouncedSearch(value)
}

const clear = () => {
  debouncedSearch.cancel()
  if (currentAbort) currentAbort.abort()
  query.value = ''
  rawFeatures.value = []
  rawMapbox.value = []
  rawNominatim.value = []
  loading.value = false
  open.value = false
  mode.value = 'search'
  pickingFor.value = null
  clearPreview()
}

// ─── Fly to ────────────────────────────────────────────────────────────────

const flyTo = (result: UnifiedResult) => {
  const m = map.value
  if (!m) return
  if (result.flyBbox) {
    m.fitBounds(result.flyBbox, { padding: 80, duration: 500 })
  } else if (result.flyCenter) {
    m.flyTo({ center: result.flyCenter, zoom: 12, duration: 500 })
  }
}

const selectResult = (result: UnifiedResult) => {
  flyTo(result)
  query.value = result.name.split(',')[0]
  open.value = false
  clearPreview()
}

// ─── Preview ───────────────────────────────────────────────────────────────

const clearPreview = () => {
  const m = map.value
  // During org switch / teardown the instance survives but its style is already
  // removed; getLayer() then dereferences an undefined style and throws, which
  // (from onBeforeUnmount) aborts the whole component update and breaks the nav.
  if (!m || !(m as any).style) return
  if (m.getLayer(`${PREVIEW_SOURCE}-fill`)) m.removeLayer(`${PREVIEW_SOURCE}-fill`)
  if (m.getLayer(`${PREVIEW_SOURCE}-line`)) m.removeLayer(`${PREVIEW_SOURCE}-line`)
  if (m.getLayer(`${PREVIEW_SOURCE}-circle`)) m.removeLayer(`${PREVIEW_SOURCE}-circle`)
  if (m.getSource(PREVIEW_SOURCE)) m.removeSource(PREVIEW_SOURCE)
  previewingId.value = null
}

const togglePreview = (result: UnifiedResult) => {
  const m = map.value
  if (!m) return
  if (previewingId.value === result.id) { clearPreview(); return }
  clearPreview()
  m.addSource(PREVIEW_SOURCE, { type: 'geojson', data: { type: 'Feature', properties: {}, geometry: result.geometry } })
  if (result.geometryType === 'Polygon' || result.geometryType === 'MultiPolygon') {
    m.addLayer({ id: `${PREVIEW_SOURCE}-fill`, type: 'fill', source: PREVIEW_SOURCE, paint: { 'fill-color': '#0059ff', 'fill-opacity': 0.15 } })
    m.addLayer({ id: `${PREVIEW_SOURCE}-line`, type: 'line', source: PREVIEW_SOURCE, paint: { 'line-color': '#0059ff', 'line-width': 2 } })
  } else {
    m.addLayer({ id: `${PREVIEW_SOURCE}-circle`, type: 'circle', source: PREVIEW_SOURCE, paint: { 'circle-color': '#0059ff', 'circle-radius': 8, 'circle-opacity': 0.8 } })
  }
  previewingId.value = result.id
  flyTo(result)
}

// ─── Add to project (geocoding results only) ───────────────────────────────

const simplifyGeometry = (geom: any) => {
  if (geom?.type !== 'Polygon' && geom?.type !== 'MultiPolygon') return geom
  return simplify({ type: 'Feature', properties: {}, geometry: geom } as any, { tolerance: SIMPLIFY_TOLERANCE, highQuality: false, mutate: false }).geometry
}

const openPicker = (result: UnifiedResult) => {
  pickingFor.value = result
  mode.value = 'pick'
}

const filterProjects = (list: Project[], search: string) =>
  list.filter(p => p.project_name?.toLowerCase().includes(search.toLowerCase()))

const addToProject = async (project: Project) => {
  const result = pickingFor.value
  if (!result) return
  const geometry = simplifyGeometry(result.geometry)
  saving.value = true
  try {
    const savedFeature = await store.dispatch('geo/saveFeature', {
      project_id: project.project_id,
      files: null,
      feature: {
        type: 'Feature',
        properties: {
          name: result.name.split(',')[0],
          description: '',
          shared: false,
          is_static: false,
          member_id: member.value?.member_id,
          created_origin: Date.now(),
          type: geometry.type,
          template_id: null,
          color: '#0059ff',
          project_id: project.project_id
        },
        geometry
      }
    })
    mode.value = 'search'
    pickingFor.value = null
    open.value = false
    clearPreview()
    router.push({
      name: 'FeatureIndex',
      params: {
        org_id: project.organization_id,
        project_id: project.project_id,
        featureCode: savedFeature.properties.code
      }
    })
  } catch {
    toast({ title: 'Failed to add', description: 'Could not save the feature.', variant: 'destructive' })
  } finally {
    saving.value = false
  }
}

// ─── Open backend feature ──────────────────────────────────────────────────

const openFeature = (result: UnifiedResult) => {
  if (!result.orgId || !result.projectId) return
  router.push({
    name: 'FeatureIndex',
    params: {
      org_id: result.orgId,
      project_id: result.projectId,
      featureCode: result.id
    }
  })
}

const handleOpenChange = (v: boolean) => {
  if (!v) { mode.value = 'search'; pickingFor.value = null; clearPreview() }
  open.value = v
}

onBeforeUnmount(() => {
  debouncedSearch.cancel()
  if (currentAbort) currentAbort.abort()
  clearPreview()
})
</script>

<template>
  <Popover :open="open" @update:open="handleOpenChange">
    <PopoverTrigger as-child>
      <div
        class="relative transition-all duration-200"
        :class="props.fullWidth ? 'w-full' : isExpanded ? 'w-[min(360px,calc(100vw-5rem))]' : 'w-48'"
      >
        <SearchIcon class="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
        <Input
          v-model="query"
          @input="onInput"
          @focus="focused = true; query && (open = true)"
          @blur="focused = false"
          @keydown="onKeydown"
          placeholder="Search the map"
          class="h-10 w-full rounded-lg shadow-md bg-background pl-9 pr-8"
        />
        <button v-if="query" type="button" class="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100" @click="clear">
          <X class="h-4 w-4" />
        </button>
      </div>
    </PopoverTrigger>

    <PopoverContent class="w-[min(360px,calc(100vw-5rem))] p-0" align="end" :side-offset="6" @open-auto-focus="(e: Event) => e.preventDefault()">

      <!-- ── Results ── -->
      <template v-if="mode === 'search'">
        <div v-if="loading && !results.length" class="flex items-center gap-2 p-4 text-sm text-gray-500">
          <Loader2 class="h-4 w-4 animate-spin" /> Searching…
        </div>
        <div v-else-if="!results.length" class="p-4 text-center text-sm text-gray-500">
          No results
        </div>
        <div v-else class="max-h-[400px] overflow-y-auto py-1">
          <template v-for="(group, gIndex) in RESULT_GROUPS" :key="group.label">
            <template v-if="results.some(r => group.types.includes(r.geometryType))">
              <div
                class="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                :class="gIndex > 0 ? 'border-t mt-1 pt-2' : ''"
              >
                {{ group.label }}
              </div>
              <div
                v-for="item in results.filter(r => group.types.includes(r.geometryType))"
                :key="item.id"
                :ref="(el) => { if (el) rowRefs[results.indexOf(item)] = el as HTMLElement }"
                class="flex items-center pl-3 pr-1 py-1 text-sm"
                :class="activeIndex === results.indexOf(item) ? 'bg-accent' : 'hover:bg-accent'"
              >
                <div class="flex items-center gap-2 flex-1 min-w-0 cursor-pointer py-1" @click="selectResult(item)">
                  <Square v-if="group.icon === 'square'" class="h-4 w-4 text-gray-400 shrink-0" />
                  <MapPin v-else class="h-4 w-4 text-gray-400 shrink-0" />
                  <span class="truncate">{{ item.name }}</span>
                  <span v-if="item.orgId && item.projectId" class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 leading-none">Project</span>
                </div>
                <!-- Backend feature: single Open button -->
                <div v-if="item.orgId && item.projectId" class="flex items-center shrink-0">
                  <Button variant="ghost" size="sm" class="h-7 w-7 p-0" title="Open feature" @click.stop="openFeature(item)">
                    <ExternalLink class="h-4 w-4" />
                  </Button>
                </div>
                <!-- Geocoding result: add to project + preview -->
                <div v-else class="flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="sm" class="h-7 w-7 p-0" title="Add to project" @click.stop="openPicker(item)">
                    <Plus class="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" class="h-7 w-7 p-0" :title="previewingId === item.id ? 'Hide preview' : 'Preview on map'" @click.stop="togglePreview(item)">
                    <Eye class="h-4 w-4 transition-colors" :class="previewingId === item.id ? 'text-primary' : 'text-foreground'" />
                  </Button>
                </div>
              </div>
            </template>
          </template>
        </div>
      </template>

      <!-- ── Project picker ── -->
      <template v-if="mode === 'pick'">
        <div class="flex items-center gap-2 px-2 py-1.5 border-b">
          <Button variant="ghost" size="sm" class="h-7 w-7 p-0" @click="mode = 'search'">
            <ArrowLeft class="h-4 w-4" />
          </Button>
          <span class="text-xs text-gray-500 truncate">
            Add geometry of <span class="font-medium text-gray-700">{{ pickingFor?.name.split(',')[0] }}</span>
          </span>
        </div>
        <Command :filter-function="filterProjects">
          <CommandInput placeholder="Search project…" />
          <CommandList>
            <CommandEmpty>No projects found</CommandEmpty>
            <CommandGroup>
              <CommandItem
                v-for="p in projects"
                :key="p.project_id"
                :value="p"
                :disabled="saving"
                @select="addToProject(p)"
              >
                <FolderDot class="h-4 w-4 mr-2 text-gray-500" />
                <span class="truncate">{{ p.project_name }}</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </template>

    </PopoverContent>
  </Popover>
</template>
